import { quotesService } from '@/services/quotes.service';

const initialState = () => ({
  quotes: [],
  status: {
    error: null,
    pending: true,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

const actions = {
  getQuotes({ commit, dispatch }, { opportunityId }) {
    commit('GET_QUOTES');
    return quotesService.getQuotes({ opportunityId }).then(
      (quotes) => {
        commit('GET_QUOTES_SUCCESS', quotes);
        return quotes;
      },
      (error) => {
        commit('GET_QUOTES_ERROR', error);
        throw error;
      },
    );
  },
  createQuote({ commit }, { opportunityId, payload }) {
    commit('CREATE_QUOTE');
    return quotesService.createQuote({ opportunityId, payload }).then(
      (quote) => {
        commit('CREATE_QUOTE_SUCCESS', quote);
        return quote;
      },
      (error) => {
        commit('CREATE_QUOTE_ERROR', error);
        throw error;
      },
    );
  },
  deleteQuote({ commit }, { opportunityId, quoteId }) {
    commit('DELETE_QUOTE');
    return quotesService.deleteQuote({ opportunityId, quoteId }).then(
      () => {
        return commit('DELETE_QUOTE_SUCCESS', quoteId);
      },
      (error) => {
        commit('DELETE_QUOTE_ERROR', error);
        throw error;
      },
    );
  },
  updateQuote({ commit }, { opportunityId, quoteId, quote }) {
    commit('UPDATE_QUOTE');
    return quotesService.updateQuote({ opportunityId, quoteId, quote }).then(
      (quote) => {
        commit('UPDATE_QUOTE_SUCCESS', quote);
        return quote;
      },
      (error) => {
        commit('UPDATE_QUOTE_ERROR', error);
        throw error;
      },
    );
  },
  async updateQuoteBulk({ commit }, { opportunityId, quotes }) {
    commit('UPDATE_QUOTE');

    // Create an array of promises for each quote update
    const updatePromises = quotes.map((quote) =>
      quotesService.updateQuote({ opportunityId, quoteId: quote.id, quote }),
    );

    // Wait for all promises to resolve
    return Promise.all(updatePromises)
      .then((updatedQuotes) => {
        // Commit the updated quotes to the store
        commit('UPDATE_QUOTE_BULK_SUCCESS', updatedQuotes);
        return updatedQuotes; // Return the updated quotes to the component
      })
      .catch((error) => {
        // Handle errors and commit the error state
        commit('UPDATE_QUOTE_ERROR', error);
        throw error; // Propagate the error to the component
      });
  },
  RESET({ commit }) {
    commit('RESET_STORE');
  },
};

const mutations = {
  GET_QUOTES(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
    state.quotes = [];
  },
  GET_QUOTES_SUCCESS(state, quotes) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: false,
    };
    state.quotes = quotes;
  },
  GET_QUOTES_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  CREATE_QUOTE() {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  CREATE_QUOTE_SUCCESS(state, quote) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: false,
    };
    state.quotes.unshift(quote);
  },
  CREATE_QUOTE_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  UPDATE_QUOTE() {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  UPDATE_QUOTE_SUCCESS(state, updatedQuote) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: false,
    };
    state.quotes = state.quotes.map((quote) => {
      if (quote.id === updatedQuote.id) {
        return updatedQuote;
      }
      return quote;
    });
  },
  UPDATE_QUOTE_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  UPDATE_QUOTE_BULK_SUCCESS(state, updatedQuotes) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: false,
    };

    // Update the quotes in the state
    updatedQuotes.forEach((updatedQuote) => {
      const index = state.quotes.findIndex((quote) => quote.id === updatedQuote.id);
      if (index !== -1) {
        state.quotes.splice(index, 1, updatedQuote); // Replace the quote in the state
      }
    });
  },
  DELETE_QUOTE() {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  DELETE_QUOTE_SUCCESS(state, quoteId) {
    state.status = {
      ...state.status,
      resolved: true,
      pending: false,
    };
    removeItemFromList(quoteId, state.quotes);
  },
  DELETE_QUOTE_ERROR(state, error) {
    state.status = {
      error: error,
      pending: false,
      resolved: false,
      rejected: true,
    };
  },
  ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  RESET_STORE(state) {
    const initial = initialState();
    Object.keys(initial).forEach((key) => {
      state[key] = initial[key];
    });
  },
};

const getters = {
  quotes: (state) => state.quotes,

  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
};

const removeItemFromList = (id, list) => {
  if (!list) return;
  const index = list.findIndex((item) => item.id === id);
  if (index !== -1) list.splice(index, 1);
  return list;
};

export const quotes = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
